import React, { useEffect, useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import character from '../../assets/images/intake/step26/char.svg';
import { useCareerReadinessContext } from '../../hooks/use-form-context.hooks';
import { usePreEmploymentAssessmentContext } from '../../hooks/use-pre-employment-assessment.hooks';
import { dispatchUpdateCareerReadiness } from '../../redux/slice/form-career-readiness';
import { AgreementLevel } from '../../redux/slice/form-employment-readiness';
import { useAppDispatch } from '../../redux/store';
import logger from '../../services/logger';

interface FormData {
  careerReadinessDefinedCareer: string;
  careerReadinessDefinedGoals: string;
  careerReadinessDefinedInterests: string;
  careerReadinessDefinedPersonality: string;
  careerReadinessKnowsAbilities: string;
  careerReadinessKnowsValues: string;
  careerReadinessKnowsStepsToTake: string;
  careerReadinessCollectingInformation: string;
  careerReadinessUnderstandsQualifications: string;
  careerReadinessAwareSuitableJobs: string;
  careerReadinessAwareSuitableOrganizations: string;
  careerReadinessKnowsCareerTrack: string;
  careerReadinessCompletedRequiredQualifications: string;
  careerReadinessIsGoalOriented: string;
  careerReadinessDevelopingExpertise: string;
  careerReadinessLimitedExperience: string;
  careerReadinessSelfAdvocate: string;
  careerReadinessDesiresPerspective: string;
  careerReadinessDesiresUnderstandingOfStrengths: string;

  careerReadinessDesiresIdentificationOfCompentencies: string;
}

interface ComponentProps {
  onBack: () => void;
  step: number;
}

const radio_options = [
  {
    label: 'Strongly Disagree',
    value: AgreementLevel.STRONGLY_DISAGREE,
  },
  {
    label: 'Disagree',
    value: AgreementLevel.DISAGREE,
  },
  {
    label: 'Somewhat',
    value: AgreementLevel.NEUTRAL,
  },
  {
    label: 'Agree',
    value: AgreementLevel.AGREE,
  },
  {
    label: 'Strongly Agree',
    value: AgreementLevel.STRONGLY_AGREE,
  },
];
export const CareerReadinessAssessment: React.FC<ComponentProps> = ({ onBack, step }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {
    careerReadinessDefinedCareer,
    careerReadinessDefinedGoals,
    careerReadinessDefinedInterests,
    careerReadinessDefinedPersonality,
    careerReadinessKnowsAbilities,
    careerReadinessKnowsValues,
    careerReadinessKnowsStepsToTake,
    careerReadinessCollectingInformation,
    careerReadinessUnderstandsQualifications,
    careerReadinessAwareSuitableJobs,
    careerReadinessAwareSuitableOrganizations,
    careerReadinessKnowsCareerTrack,
    careerReadinessCompletedRequiredQualifications,
    careerReadinessIsGoalOriented,
    careerReadinessDevelopingExpertise,
    careerReadinessLimitedExperience,
    careerReadinessSelfAdvocate,
    careerReadinessDesiresPerspective,
    careerReadinessDesiresUnderstandingOfStrengths,
    careerReadinessDesiresIdentificationOfCompentencies,
  } = useCareerReadinessContext();
  const [selectedDefinedCareer, setSelectedDefinedCareer] = useState(
    careerReadinessDefinedCareer || '',
  );
  const [selectedDefinedGoals, setSelectedDefinedGoals] = useState(
    careerReadinessDefinedGoals || '',
  );
  const [selectedDefinedInterests, setSelectedDefinedInterests] = useState(
    careerReadinessDefinedInterests || '',
  );
  const [selectedDefinedPersonality, setSelectedDefinedPersonality] = useState(
    careerReadinessDefinedPersonality || '',
  );
  const [selectedKnowsAbilities, setSelectedKnowsAbilities] = useState(
    careerReadinessKnowsAbilities || '',
  );
  const [selectedKnowsValues, setSelectedKnowsValues] = useState(careerReadinessKnowsValues || '');
  const [selectedKnowsStepsToTake, setSelectedKnowsStepsToTake] = useState(
    careerReadinessKnowsStepsToTake || '',
  );
  const [selectedCollectingInformation, setSelectedCollectingInformation] = useState(
    careerReadinessCollectingInformation || '',
  );
  const [selectedUnderstandsQualifications, setSelectedUnderstandsQualifications] = useState(
    careerReadinessUnderstandsQualifications || '',
  );

  const [selectedAwareSuitableJobs, setSelectedAwareSuitableJobs] = useState(
    careerReadinessAwareSuitableJobs || '',
  );
  const [selectedAwareSuitableOrganizations, setSelectedAwareSuitableOrganizations] = useState(
    careerReadinessAwareSuitableOrganizations || '',
  );
  const [selectedKnowsCareerTrack, setSelectedKnowsCareerTrack] = useState(
    careerReadinessKnowsCareerTrack || '',
  );
  const [selectedCompletedRequiredQualifications, setSelectedCompletedRequiredQualifications] =
    useState(careerReadinessCompletedRequiredQualifications || '');
  const [selectedIsGoalOriented, setSelectedIsGoalOriented] = useState(
    careerReadinessIsGoalOriented || '',
  );
  const [selectedDevelopingExpertise, setSelectedDevelopingExpertise] = useState(
    careerReadinessDevelopingExpertise || '',
  );

  const [selectedLimitedExperience, setSelectedLimitedExperience] = useState(
    careerReadinessLimitedExperience || '',
  );
  const [selectedSelfAdvocate, setSelectedSelfAdvocate] = useState(
    careerReadinessSelfAdvocate || '',
  );

  const [selectedDesiresPerspective, setSelectedDesiresPerspective] = useState(
    careerReadinessDesiresPerspective || '',
  );
  const [selectedDesiresUnderstandingOfStrengths, setSelectedDesiresUnderstandingOfStrengths] =
    useState(careerReadinessDesiresUnderstandingOfStrengths || '');
  const [
    selectedDesiresIdentificationOfCompentencies,
    setSelectedDesiresIdentificationOfCompentencies,
  ] = useState(careerReadinessDesiresIdentificationOfCompentencies || '');

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>, field: string) => {
    switch (field as keyof FormData) {
      case 'careerReadinessDefinedCareer':
        setSelectedDefinedCareer(event.target.value);
        setError('');
        break;
      case 'careerReadinessDefinedGoals':
        setSelectedDefinedGoals(event.target.value);
        setError('');
        break;
      case 'careerReadinessDefinedInterests':
        setSelectedDefinedInterests(event.target.value);
        setError('');
        break;
      case 'careerReadinessDefinedPersonality':
        setSelectedDefinedPersonality(event.target.value);
        setError('');
        break;
      case 'careerReadinessKnowsAbilities':
        setSelectedKnowsAbilities(event.target.value);
        setError('');
        break;
      case 'careerReadinessKnowsValues':
        setSelectedKnowsValues(event.target.value);
        setError('');
        break;
      case 'careerReadinessKnowsStepsToTake':
        setSelectedKnowsStepsToTake(event.target.value);
        setError('');
        break;
      case 'careerReadinessCollectingInformation':
        setSelectedCollectingInformation(event.target.value);
        setError('');
        break;
      case 'careerReadinessUnderstandsQualifications':
        setSelectedUnderstandsQualifications(event.target.value);
        setError('');
        break;

      case 'careerReadinessAwareSuitableJobs':
        setSelectedAwareSuitableJobs(event.target.value);
        setError('');
        break;
      case 'careerReadinessAwareSuitableOrganizations':
        setSelectedAwareSuitableOrganizations(event.target.value);
        setError('');
        break;
      case 'careerReadinessKnowsCareerTrack':
        setSelectedKnowsCareerTrack(event.target.value);
        setError('');
        break;
      case 'careerReadinessCompletedRequiredQualifications':
        setSelectedCompletedRequiredQualifications(event.target.value);
        setError('');
        break;
      case 'careerReadinessIsGoalOriented':
        setSelectedIsGoalOriented(event.target.value);
        setError('');
        break;
      case 'careerReadinessDevelopingExpertise':
        setSelectedDevelopingExpertise(event.target.value);
        setError('');
        break;
      case 'careerReadinessLimitedExperience':
        setSelectedLimitedExperience(event.target.value);
        setError('');
        break;
      case 'careerReadinessSelfAdvocate':
        setSelectedSelfAdvocate(event.target.value);
        setError('');
        break;

      case 'careerReadinessDesiresPerspective':
        setSelectedDesiresPerspective(event.target.value);
        setError('');
        break;
      case 'careerReadinessDesiresUnderstandingOfStrengths':
        setSelectedDesiresUnderstandingOfStrengths(event.target.value);
        setError('');
        break;
      case 'careerReadinessDesiresIdentificationOfCompentencies':
        setSelectedDesiresIdentificationOfCompentencies(event.target.value);
        setError('');
        break;
    }
  };

  const [error, setError] = useState('');
  const { handleSubmit } = useForm<FormData>();
  const backButtonClick = () => {
    onBack();
  };
  const { requiresCareerReadinessAssessment } = usePreEmploymentAssessmentContext();
  const submitAssessment: SubmitHandler<FormData> = async () => {
    logger.debug('Submitted career readiness form.', {
      careerReadinessDefinedCareer,
      careerReadinessDefinedGoals,
      careerReadinessDefinedInterests,
      careerReadinessDefinedPersonality,
      careerReadinessKnowsAbilities,
      careerReadinessKnowsValues,
      careerReadinessKnowsStepsToTake,
      careerReadinessCollectingInformation,
      careerReadinessUnderstandsQualifications,
      careerReadinessAwareSuitableJobs,
      careerReadinessAwareSuitableOrganizations,
      careerReadinessKnowsCareerTrack,
      careerReadinessCompletedRequiredQualifications,
      careerReadinessIsGoalOriented,
      careerReadinessDevelopingExpertise,
      careerReadinessLimitedExperience,
      careerReadinessSelfAdvocate,
      careerReadinessDesiresPerspective,
      careerReadinessDesiresUnderstandingOfStrengths,
      careerReadinessDesiresIdentificationOfCompentencies,
    });
    const error_message: string = 'Please select an for each statements';
    if (
      !selectedDefinedCareer ||
      !selectedDefinedInterests ||
      !selectedDefinedGoals ||
      !selectedDefinedPersonality ||
      !selectedKnowsAbilities ||
      !selectedKnowsValues ||
      !selectedKnowsStepsToTake ||
      !selectedCollectingInformation ||
      !selectedUnderstandsQualifications ||
      !selectedAwareSuitableJobs ||
      !selectedAwareSuitableOrganizations ||
      !selectedKnowsCareerTrack ||
      !selectedCompletedRequiredQualifications ||
      !selectedIsGoalOriented ||
      !selectedDevelopingExpertise ||
      !selectedLimitedExperience ||
      !selectedSelfAdvocate ||
      !selectedDesiresPerspective ||
      !selectedDesiresUnderstandingOfStrengths ||
      !selectedDesiresIdentificationOfCompentencies
    ) {
      setError(error_message);
    } else {
      const dto: Record<keyof FormData, AgreementLevel> = {
        careerReadinessDefinedCareer: selectedDefinedCareer as AgreementLevel,
        careerReadinessDefinedGoals: selectedDefinedGoals as AgreementLevel,
        careerReadinessDefinedInterests: selectedDefinedInterests as AgreementLevel,
        careerReadinessDefinedPersonality: selectedDefinedPersonality as AgreementLevel,
        careerReadinessKnowsAbilities: selectedKnowsAbilities as AgreementLevel,
        careerReadinessKnowsValues: selectedKnowsValues as AgreementLevel,
        careerReadinessKnowsStepsToTake: selectedKnowsStepsToTake as AgreementLevel,
        careerReadinessCollectingInformation: selectedCollectingInformation as AgreementLevel,
        careerReadinessUnderstandsQualifications:
          selectedUnderstandsQualifications as AgreementLevel,
        careerReadinessAwareSuitableJobs: selectedAwareSuitableJobs as AgreementLevel,
        careerReadinessAwareSuitableOrganizations:
          selectedAwareSuitableOrganizations as AgreementLevel,
        careerReadinessKnowsCareerTrack: selectedKnowsCareerTrack as AgreementLevel,
        careerReadinessCompletedRequiredQualifications:
          selectedCompletedRequiredQualifications as AgreementLevel,
        careerReadinessIsGoalOriented: selectedIsGoalOriented as AgreementLevel,
        careerReadinessDevelopingExpertise: selectedDevelopingExpertise as AgreementLevel,
        careerReadinessLimitedExperience: selectedLimitedExperience as AgreementLevel,
        careerReadinessSelfAdvocate: selectedSelfAdvocate as AgreementLevel,
        careerReadinessDesiresPerspective: selectedDesiresPerspective as AgreementLevel,
        careerReadinessDesiresUnderstandingOfStrengths:
          selectedDesiresUnderstandingOfStrengths as AgreementLevel,
        careerReadinessDesiresIdentificationOfCompentencies:
          selectedDesiresIdentificationOfCompentencies as AgreementLevel,
      };
      await dispatch(dispatchUpdateCareerReadiness(dto));
      if (!requiresCareerReadinessAssessment) {
        navigate('/pre-employment/experience/step1');
      } else {
        navigate(`/pre-employment/career-readiness/step${step + 1}`);
      }
    }
  };

  useEffect(() => {
    if (careerReadinessDefinedCareer) {
      setSelectedDefinedCareer(careerReadinessDefinedCareer);
    }
    if (careerReadinessDefinedGoals) {
      setSelectedDefinedGoals(careerReadinessDefinedGoals);
    }
    if (careerReadinessDefinedInterests) {
      setSelectedDefinedInterests(careerReadinessDefinedInterests);
    }
    if (careerReadinessDefinedPersonality) {
      setSelectedDefinedPersonality(careerReadinessDefinedPersonality);
    }
    if (careerReadinessKnowsAbilities) {
      setSelectedKnowsAbilities(careerReadinessKnowsAbilities);
    }
    if (careerReadinessKnowsValues) {
      setSelectedKnowsValues(careerReadinessKnowsValues);
    }
    if (careerReadinessKnowsStepsToTake) {
      setSelectedKnowsStepsToTake(careerReadinessKnowsStepsToTake);
    }
    if (careerReadinessCollectingInformation) {
      setSelectedCollectingInformation(careerReadinessCollectingInformation);
    }
    if (careerReadinessUnderstandsQualifications) {
      setSelectedUnderstandsQualifications(careerReadinessUnderstandsQualifications);
    }
    if (careerReadinessAwareSuitableJobs) {
      setSelectedAwareSuitableJobs(careerReadinessAwareSuitableJobs);
    }
    if (careerReadinessAwareSuitableOrganizations) {
      setSelectedAwareSuitableOrganizations(careerReadinessAwareSuitableOrganizations);
    }
    if (careerReadinessKnowsCareerTrack) {
      setSelectedKnowsCareerTrack(careerReadinessKnowsCareerTrack);
    }
    if (careerReadinessCompletedRequiredQualifications) {
      setSelectedCompletedRequiredQualifications(careerReadinessCompletedRequiredQualifications);
    }
    if (careerReadinessIsGoalOriented) {
      setSelectedIsGoalOriented(careerReadinessIsGoalOriented);
    }
    if (careerReadinessDevelopingExpertise) {
      setSelectedDevelopingExpertise(careerReadinessDevelopingExpertise);
    }
    if (careerReadinessLimitedExperience) {
      setSelectedLimitedExperience(careerReadinessLimitedExperience);
    }
    if (careerReadinessSelfAdvocate) {
      setSelectedSelfAdvocate(careerReadinessSelfAdvocate);
    }
    if (careerReadinessDesiresPerspective) {
      setSelectedDesiresPerspective(careerReadinessDesiresPerspective);
    }
    if (careerReadinessDesiresUnderstandingOfStrengths) {
      setSelectedDesiresUnderstandingOfStrengths(careerReadinessDesiresUnderstandingOfStrengths);
    }
    if (careerReadinessDesiresIdentificationOfCompentencies) {
      setSelectedDesiresIdentificationOfCompentencies(
        careerReadinessDesiresIdentificationOfCompentencies,
      );
    }
  }, [
    careerReadinessDefinedCareer,
    careerReadinessDefinedGoals,
    careerReadinessDefinedInterests,
    careerReadinessDefinedPersonality,
    careerReadinessKnowsAbilities,
    careerReadinessKnowsValues,
    careerReadinessKnowsStepsToTake,
    careerReadinessCollectingInformation,
    careerReadinessUnderstandsQualifications,
    careerReadinessAwareSuitableJobs,
    careerReadinessAwareSuitableOrganizations,
    careerReadinessKnowsCareerTrack,
    careerReadinessCompletedRequiredQualifications,
    careerReadinessIsGoalOriented,
    careerReadinessDevelopingExpertise,
    careerReadinessLimitedExperience,
    careerReadinessSelfAdvocate,
    careerReadinessDesiresPerspective,
    careerReadinessDesiresUnderstandingOfStrengths,
    careerReadinessDesiresIdentificationOfCompentencies,
  ]);

  return (
    <div>
      <p className="text-lg mt-7 md:px-10">
        Career readiness is the awareness and development of the skills one needs to confidently and
        successfully meet employer expectations and transition into work in your career choice.
      </p>
      <div className="flex items-center mt-9 mb-10 md:px-10">
        <div className="bg-gray-200 p-4 rounded-lg font-semibold mr-5 flex items-center">27</div>
        <div>
          <h4 className="text-2xl">
            To what extent
            <span className="font-semibold"> do you agree or disagree </span>
            with the following statements?
          </h4>
        </div>
      </div>
      <div className="character absolute top-96 right-[-211px] z-[-1]">
        <img className="" src={character} alt="" />
      </div>
      <form
        className=" md:px-16"
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={handleSubmit(submitAssessment)}
      >
        <ol style={{ listStyle: 'upper-latin' }} type="A">
          <li className="mb-5 font-semibold">
            I understand the career I want to pursue.
            <div className="rating-btns flex items-center mt-3">
              {radio_options.map((option) => (
                <div key={option.value} className="h-[55px] mr-4">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedDefinedCareer === option.value}
                      onChange={(event) => handleRadioChange(event, 'careerReadinessDefinedCareer')}
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="careerReadinessDefinedCareer"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
            </div>
          </li>
          <li className="mb-5 font-semibold">
            I have clearly defined career goals.
            <div className="rating-btns flex items-center mt-3">
              {radio_options.map((option) => (
                <div key={option.value} className="h-[55px] mr-4">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedDefinedGoals === option.value}
                      onChange={(event) => handleRadioChange(event, 'careerReadinessDefinedGoals')}
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="careerReadinessDefinedGoals"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
            </div>
          </li>
          <li className="mb-5 font-semibold">
            I have explored and understand my own career interests.
            <div className="rating-btns flex items-center mt-3">
              {radio_options.map((option) => (
                <div key={option.value} className="h-[55px] mr-4">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedDefinedInterests === option.value}
                      onChange={(event) =>
                        handleRadioChange(event, 'careerReadinessDefinedInterests')
                      }
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="careerReadinessDefinedInterests"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
            </div>
          </li>
          <li className="mb-5 font-semibold">
            I have explored my personality type and understand the careers that fit my type.
            <div className="rating-btns flex items-center mt-3">
              {radio_options.map((option) => (
                <div key={option.value} className="h-[55px] mr-4">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedDefinedPersonality === option.value}
                      onChange={(event) =>
                        handleRadioChange(event, 'careerReadinessDefinedPersonality')
                      }
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="careerReadinessDefinedPersonality"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
            </div>
          </li>
          <li className="mb-5 font-semibold">
            I have explored by abilities and how they related to my career interests and personality
            type.
            <div className="rating-btns flex items-center mt-3">
              {radio_options.map((option) => (
                <div key={option.value} className="h-[55px] mr-4">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedKnowsAbilities === option.value}
                      onChange={(event) =>
                        handleRadioChange(event, 'careerReadinessKnowsAbilities')
                      }
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="careerReadinessKnowsAbilities"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
            </div>
          </li>
          <li className="mb-5 font-semibold">
            I have explored my work values and know what is important to me in a career.
            <div className="rating-btns flex items-center mt-3">
              {radio_options.map((option) => (
                <div key={option.value} className="h-[55px] mr-4">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedKnowsValues === option.value}
                      onChange={(event) => handleRadioChange(event, 'careerReadinessKnowsValues')}
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="careerReadinessKnowsValues"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
            </div>
          </li>
          <li className="mb-5 font-semibold">
            I know the steps I need to take to achieve my career goals.
            <div className="rating-btns flex items-center mt-3">
              {radio_options.map((option) => (
                <div key={option.value} className="h-[55px] mr-4">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedKnowsStepsToTake === option.value}
                      onChange={(event) =>
                        handleRadioChange(event, 'careerReadinessKnowsStepsToTake')
                      }
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="careerReadinessKnowsStepsToTake"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
            </div>
          </li>
          <li className="mb-5 font-semibold">
            I have started collecting information related to my career goals.
            <div className="rating-btns flex items-center mt-3">
              {radio_options.map((option) => (
                <div key={option.value} className="h-[55px] mr-4">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedCollectingInformation === option.value}
                      onChange={(event) =>
                        handleRadioChange(event, 'careerReadinessCollectingInformation')
                      }
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="careerReadinessCollectingInformation"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
            </div>
          </li>
          <li className="mb-5 font-semibold">
            I know what skills, qualifications and characterstics employers expect in my desired
            career field.
            <div className="rating-btns flex items-center mt-3">
              {radio_options.map((option) => (
                <div key={option.value} className="h-[55px] mr-4">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedUnderstandsQualifications === option.value}
                      onChange={(event) =>
                        handleRadioChange(event, 'careerReadinessUnderstandsQualifications')
                      }
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="careerReadinessUnderstandsQualifications"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
            </div>
          </li>
          <li className="mb-5 font-semibold">
            I know the kind of job(s) that is suitable for me.
            <div className="rating-btns flex items-center mt-3">
              {radio_options.map((option) => (
                <div key={option.value} className="h-[55px] mr-4">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedAwareSuitableJobs === option.value}
                      onChange={(event) =>
                        handleRadioChange(event, 'careerReadinessAwareSuitableJobs')
                      }
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="careerReadinessAwareSuitableJobs"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
            </div>
          </li>
          <li className="mb-5 font-semibold">
            I know what kind of organizations are out there doing the kind of work I'm interested in
            <div className="rating-btns flex items-center mt-3">
              {radio_options.map((option) => (
                <div key={option.value} className="h-[55px] mr-4">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedAwareSuitableOrganizations === option.value}
                      onChange={(event) =>
                        handleRadioChange(event, 'careerReadinessAwareSuitableOrganizations')
                      }
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="careerReadinessAwareSuitableOrganizations"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
            </div>
          </li>
          <li className="mb-5 font-semibold">
            I know what a typical career track in this field looks like?
            <div className="rating-btns flex items-center mt-3">
              {radio_options.map((option) => (
                <div key={option.value} className="h-[55px] mr-4">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedKnowsCareerTrack === option.value}
                      onChange={(event) =>
                        handleRadioChange(event, 'careerReadinessKnowsCareerTrack')
                      }
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="careerReadinessKnowsCareerTrack"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
            </div>
          </li>
          <li className="mb-5 font-semibold">
            I have completed the required high school subjects, licences, and/or certification I
            will need in my career choice
            <div className="rating-btns flex items-center mt-3">
              {radio_options.map((option) => (
                <div key={option.value} className="h-[55px] mr-4">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedCompletedRequiredQualifications === option.value}
                      onChange={(event) =>
                        handleRadioChange(event, 'careerReadinessCompletedRequiredQualifications')
                      }
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="careerReadinessCompletedRequiredQualifications"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
            </div>
          </li>
          <li className="mb-5 font-semibold">
            I move towards my goal, regardless of what others may think.
            <div className="rating-btns flex items-center mt-3">
              {radio_options.map((option) => (
                <div key={option.value} className="h-[55px] mr-4">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedIsGoalOriented === option.value}
                      onChange={(event) =>
                        handleRadioChange(event, 'careerReadinessIsGoalOriented')
                      }
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="careerReadinessIsGoalOriented"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
            </div>
          </li>
          <li className="mb-5 font-semibold">
            I spend time on developing my expertise and interest.
            <div className="rating-btns flex items-center mt-3">
              {radio_options.map((option) => (
                <div key={option.value} className="h-[55px] mr-4">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedDevelopingExpertise === option.value}
                      onChange={(event) =>
                        handleRadioChange(event, 'careerReadinessDevelopingExpertise')
                      }
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="careerReadinessDevelopingExpertise"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
            </div>
          </li>
          <li className="mb-5 font-semibold">
            I think my relevant working experience is too limited.
            <div className="rating-btns flex items-center mt-3">
              {radio_options.map((option) => (
                <div key={option.value} className="h-[55px] mr-4">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedLimitedExperience === option.value}
                      onChange={(event) =>
                        handleRadioChange(event, 'careerReadinessLimitedExperience')
                      }
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="careerReadinessLimitedExperience"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
            </div>
          </li>
          <li className="mb-5 font-semibold">
            I know how to self-advocate for opportunities in the workplace.
            <div className="rating-btns flex items-center mt-3">
              {radio_options.map((option) => (
                <div key={option.value} className="h-[55px] mr-4">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedSelfAdvocate === option.value}
                      onChange={(event) => handleRadioChange(event, 'careerReadinessSelfAdvocate')}
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="careerReadinessSelfAdvocate"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
            </div>
          </li>
          <li className="mb-5 font-semibold">
            I'd like a stronger perspective regarding future job potential.
            <div className="rating-btns flex items-center mt-3">
              {radio_options.map((option) => (
                <div key={option.value} className="h-[55px] mr-4">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedDesiresPerspective === option.value}
                      onChange={(event) =>
                        handleRadioChange(event, 'careerReadinessDesiresPerspective')
                      }
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="careerReadinessDesiresPerspective"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
            </div>
          </li>
          <li className="mb-5 font-semibold">
            I'd like a clearer picture of my strengths, interests, values, and personality type.
            <div className="rating-btns flex items-center mt-3">
              {radio_options.map((option) => (
                <div key={option.value} className="h-[55px] mr-4">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedDesiresUnderstandingOfStrengths === option.value}
                      onChange={(event) =>
                        handleRadioChange(event, 'careerReadinessDesiresUnderstandingOfStrengths')
                      }
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="careerReadinessDesiresUnderstandingOfStrengths"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
            </div>
          </li>
          <li className="mb-5 font-semibold">
            I want identify competency areas that I can further develop.
            <div className="rating-btns flex items-center mt-3">
              {radio_options.map((option) => (
                <div key={option.value} className="h-[55px] mr-4">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedDesiresIdentificationOfCompentencies === option.value}
                      onChange={(event) =>
                        handleRadioChange(
                          event,
                          'careerReadinessDesiresIdentificationOfCompentencies',
                        )
                      }
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="careerReadinessDesiresIdentificationOfCompentencies"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
            </div>
          </li>
        </ol>
        {error && <p className="text-red-500 font-light !text-sm pt-5">{error}</p>}
        <div className="flex justify-between mt-20">
          <button
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onClick={handleSubmit(backButtonClick)}
            className="btn-primary"
          >
            Back
          </button>
          <button className="btn-primary">Next</button>
        </div>
      </form>
    </div>
  );
};
